// Convert a limited subset of HTML/markdown-ish text into block nodes for PDF/Word export.
// Supported blocks: heading(level), paragraph(runs), list(type, items), table(rows)

import {
  joinFragmentedHeadingContinuationInHtml,
  joinFragmentedHeadingContinuationLines,
  isPartialProtocolHeadingLine,
  isMainSectionTitleContinuationLine,
  promoteProtocolSubsectionHeadingsInHtml,
  findEmbeddedSubsectionHeading,
  isSubsectionHeadingContinuationLine,
  splitColonObjectiveHeadingLine,
} from './protocolExportContent';
import { parseProtocolHeadingLine } from './protocolHeadingLineUtils';

export function richTextToBlocks(input, { sectionRootPrefix = null } = {}) {
  if (!input) return [];
  let text = String(input);
  const containsTag = /<\w+[^>]*>/i.test(text);

  if (containsTag) {
    text = joinFragmentedHeadingContinuationInHtml(text, sectionRootPrefix);
    text = promoteProtocolSubsectionHeadingsInHtml(text, sectionRootPrefix);
    text = text.replace(/<br\s*\/?>/gi, '\n');
    try {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      mergeAdjacentPartialHeadingParagraphsInDom(doc, sectionRootPrefix);
      return Array.from(doc.body.childNodes).flatMap((node) =>
        domNodeToBlocks(node, sectionRootPrefix),
      );
    } catch (_) {
      return plainToParagraphs(text, sectionRootPrefix);
    }
  }
  return plainToParagraphs(text, sectionRootPrefix);
}

function mergeAdjacentPartialHeadingParagraphsInDom(doc, sectionRootPrefix) {
  if (!doc?.body) return;
  const elements = Array.from(doc.body.children);
  for (let i = 0; i < elements.length - 1; ) {
    const el = elements[i];
    const next = elements[i + 1];
    if (!el || !next) break;
    if (el.tagName?.toLowerCase() !== 'p' && el.tagName?.toLowerCase() !== 'div') {
      i += 1;
      continue;
    }
    if (next.tagName?.toLowerCase() !== 'p' && next.tagName?.toLowerCase() !== 'div') {
      i += 1;
      continue;
    }
    const cur = el.textContent.replace(/\s+/g, ' ').trim();
    const nxt = next.textContent.replace(/\s+/g, ' ').trim();
    if (
      /^\d+(?:\.\d+)+\.?\s*$/.test(cur) ||
      isPartialProtocolHeadingLine(cur, sectionRootPrefix) ||
      isSubsectionHeadingContinuationLine(cur, nxt) ||
      isMainSectionTitleContinuationLine(cur, nxt)
    ) {
      el.textContent = `${cur} ${nxt}`;
      next.remove();
      elements.splice(i + 1, 1);
      continue;
    }
    i += 1;
  }
}

function headingLevelFromProtocolText(text, sectionRootPrefix) {
  const parsed = parseProtocolHeadingLine(text, { sectionRootPrefix });
  if (!parsed) return null;
  if (parsed.kind === 'main') return 1;
  const dotCount = (parsed.number.match(/\./g) || []).length;
  return Math.min(1 + dotCount, 4);
}

function plainToParagraphs(text, sectionRootPrefix = null) {
  const joined = joinFragmentedHeadingContinuationLines(String(text), sectionRootPrefix);
  const blocks = [];
  const lines = joined.split(/\n/);
  let paragraphBuffer = [];

  const flushParagraph = () => {
    const merged = paragraphBuffer.join(' ').replace(/\s+/g, ' ').trim();
    paragraphBuffer = [];
    if (!merged) return;
    const level = headingLevelFromProtocolText(merged, sectionRootPrefix);
    if (level != null && !isPartialProtocolHeadingLine(merged, sectionRootPrefix)) {
      blocks.push({ type: 'heading', level, text: merged });
      return;
    }
    blocks.push({ type: 'paragraph', runs: [{ text: merged }] });
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].replace(/\s+$/, '').replace(/^\s+/, '');
    if (!line) {
      flushParagraph();
      continue;
    }

    const level = headingLevelFromProtocolText(line, sectionRootPrefix);
    if (level != null && !isPartialProtocolHeadingLine(line, sectionRootPrefix)) {
      flushParagraph();
      blocks.push({ type: 'heading', level, text: line });
      continue;
    }

    const listMatch = line.match(/^\s*(?:•|-|\*)\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      blocks.push({ type: 'list', ordered: false, items: [[{ text: listMatch[1] }]] });
      continue;
    }

    paragraphBuffer.push(line);
  }
  flushParagraph();
  return blocks;
}

function splitParagraphDomNodeToBlocks(node, sectionRootPrefix = null) {
  const plain = node.textContent.replace(/\s+/g, ' ').trim();
  const colon = splitColonObjectiveHeadingLine(plain);
  if (colon && parseProtocolHeadingLine(colon.headingLine, { sectionRootPrefix })) {
    const blocks = [
      {
        type: 'heading',
        level: headingLevelFromProtocolText(colon.headingLine, sectionRootPrefix),
        text: colon.headingLine,
      },
    ];
    if (colon.remainder) blocks.push({ type: 'paragraph', runs: [{ text: colon.remainder }] });
    return blocks;
  }

  const embedded = findEmbeddedSubsectionHeading(plain, sectionRootPrefix);
  if (embedded) {
    const blocks = [];
    if (embedded.before) blocks.push({ type: 'paragraph', runs: [{ text: embedded.before }] });
    blocks.push({
      type: 'heading',
      level: headingLevelFromProtocolText(embedded.heading, sectionRootPrefix),
      text: embedded.heading,
    });
    if (embedded.after) blocks.push({ type: 'paragraph', runs: [{ text: embedded.after }] });
    return blocks;
  }
  return [{ type: 'paragraph', runs: inlineRuns(node) }];
}

function domNodeToBlocks(node, sectionRootPrefix = null) {
  if (node.nodeType === 3) {
    const t = node.textContent.replace(/\s+/g, ' ').trim();
    if (!t) return [];
    const level = headingLevelFromProtocolText(t, sectionRootPrefix);
    if (level != null && !isPartialProtocolHeadingLine(t, sectionRootPrefix)) {
      return [{ type: 'heading', level, text: t }];
    }
    return [{ type: 'paragraph', runs: [{ text: t }] }];
  }
  if (node.nodeType !== 1) return [];
  const tag = node.tagName.toLowerCase();

  if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
    const text = node.textContent.replace(/\s+/g, ' ').trim();
    const protocolLevel = headingLevelFromProtocolText(text, sectionRootPrefix);
    const level = protocolLevel ?? parseInt(tag[1], 10);
    return [{ type: 'heading', level, text }];
  }

  if (tag === 'p' || tag === 'div') {
    const text = node.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return [];
    const level = headingLevelFromProtocolText(text, sectionRootPrefix);
    if (level != null && !isPartialProtocolHeadingLine(text, sectionRootPrefix)) {
      return [{ type: 'heading', level, text }];
    }

    const brParts = String(node.innerHTML || '')
      .split(/<br\s*\/?>/i)
      .map((part) => part.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (brParts.length > 1) {
      const headLevel = headingLevelFromProtocolText(brParts[0], sectionRootPrefix);
      if (headLevel != null && !isPartialProtocolHeadingLine(brParts[0], sectionRootPrefix)) {
        const blocks = [{ type: 'heading', level: headLevel, text: brParts[0] }];
        const tail = brParts.slice(1).join(' ').trim();
        if (tail) blocks.push({ type: 'paragraph', runs: [{ text: tail }] });
        return blocks;
      }
    }

    return splitParagraphDomNodeToBlocks(node, sectionRootPrefix);
  }

  if (tag === 'br') return [];

  if (tag === 'ul' || tag === 'ol') {
    const items = Array.from(node.querySelectorAll(':scope > li')).map((li) => inlineRuns(li));
    return [{ type: 'list', ordered: tag === 'ol', items }];
  }

  if (tag === 'table') {
    // A rowspanned cell (e.g. an Objective Type cell spanning several
    // Secondary/Exploratory rows) has no <td> of its own in the rows it
    // covers — that's how HTML rowspan works. Without carrying rowspan/
    // colspan through, docx table export (SAPEditorUsdm.jsx) has no way to
    // know a column was skipped, so the shorter row's remaining cells get
    // built into the wrong docx table columns (Objective text ends up under
    // Objective Type, Endpoint under Objective) even though the on-screen
    // HTML preview — which the browser lays out natively — renders the
    // merge correctly. rowSpan/colSpan are attached directly on the
    // returned runs array (which stays a plain array for every existing
    // consumer that just iterates cell runs) so docx's TableCell can
    // declare them; docx's own Table builder then auto-inserts the
    // vertical-merge continuation cells into the following rows.
    const rows = Array.from(node.querySelectorAll('tr')).map((tr) =>
      Array.from(tr.querySelectorAll('th,td')).map((td) => {
        const runs = inlineRuns(td);
        const rowSpan = Math.max(1, parseInt(td.getAttribute('rowspan') || '1', 10) || 1);
        const colSpan = Math.max(1, parseInt(td.getAttribute('colspan') || '1', 10) || 1);
        if (rowSpan > 1) runs.rowSpan = rowSpan;
        if (colSpan > 1) runs.colSpan = colSpan;
        return runs;
      }),
    );
    return [{ type: 'table', rows, outerHtml: node.outerHTML }];
  }

  return Array.from(node.childNodes).flatMap((child) => domNodeToBlocks(child, sectionRootPrefix));
}

function inlineRuns(container) {
  const runs = [];
  container.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      const t = child.textContent.replace(/\s+/g, ' ');
      if (t) runs.push({ text: t });
    } else if (child.nodeType === 1) {
      const childTag = child.tagName.toLowerCase();
      const inner = inlineRuns(child);
      if (childTag === 'strong' || childTag === 'b') inner.forEach((r) => (r.bold = true));
      if (childTag === 'em' || childTag === 'i') inner.forEach((r) => (r.italic = true));
      runs.push(...inner);
    }
  });
  return mergeRuns(runs);
}

function mergeRuns(runs) {
  const out = [];
  for (const r of runs) {
    const prev = out[out.length - 1];
    if (prev && !!prev.bold === !!r.bold && !!prev.italic === !!r.italic) {
      prev.text += r.text;
    } else {
      out.push({ ...r });
    }
  }
  return out;
}
